import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Check, ChevronDown, Search } from "lucide-react";

// Common countries with their codes and flags
const countries = [
    { code: "+91", name: "India", flag: "🇮🇳", short: "IN" },
    { code: "+1", name: "United States", flag: "🇺🇸", short: "US" },
    { code: "+44", name: "United Kingdom", flag: "🇬🇧", short: "GB" },
    { code: "+971", name: "UAE", flag: "🇦🇪", short: "AE" },
    { code: "+65", name: "Singapore", flag: "🇸🇬", short: "SG" },
    { code: "+61", name: "Australia", flag: "🇦🇺", short: "AU" },
    { code: "+49", name: "Germany", flag: "🇩🇪", short: "DE" },
    { code: "+33", name: "France", flag: "🇫🇷", short: "FR" },
    { code: "+81", name: "Japan", flag: "🇯🇵", short: "JP" },
    { code: "+86", name: "China", flag: "🇨🇳", short: "CN" },
    { code: "+82", name: "South Korea", flag: "🇰🇷", short: "KR" },
    { code: "+55", name: "Brazil", flag: "🇧🇷", short: "BR" },
    { code: "+52", name: "Mexico", flag: "🇲🇽", short: "MX" },
    { code: "+7", name: "Russia", flag: "🇷🇺", short: "RU" },
    { code: "+27", name: "South Africa", flag: "🇿🇦", short: "ZA" },
    { code: "+966", name: "Saudi Arabia", flag: "🇸🇦", short: "SA" },
    { code: "+31", name: "Netherlands", flag: "🇳🇱", short: "NL" },
    { code: "+39", name: "Italy", flag: "🇮🇹", short: "IT" },
    { code: "+34", name: "Spain", flag: "🇪🇸", short: "ES" },
    { code: "+41", name: "Switzerland", flag: "🇨🇭", short: "CH" },
    { code: "+46", name: "Sweden", flag: "🇸🇪", short: "SE" },
    { code: "+47", name: "Norway", flag: "🇳🇴", short: "NO" },
    { code: "+45", name: "Denmark", flag: "🇩🇰", short: "DK" },
    { code: "+358", name: "Finland", flag: "🇫🇮", short: "FI" },
    { code: "+60", name: "Malaysia", flag: "🇲🇾", short: "MY" },
    { code: "+63", name: "Philippines", flag: "🇵🇭", short: "PH" },
    { code: "+66", name: "Thailand", flag: "🇹🇭", short: "TH" },
    { code: "+84", name: "Vietnam", flag: "🇻🇳", short: "VN" },
    { code: "+62", name: "Indonesia", flag: "🇮🇩", short: "ID" },
    { code: "+64", name: "New Zealand", flag: "🇳🇿", short: "NZ" },
    { code: "+353", name: "Ireland", flag: "🇮🇪", short: "IE" },
    { code: "+48", name: "Poland", flag: "🇵🇱", short: "PL" },
    { code: "+43", name: "Austria", flag: "🇦🇹", short: "AT" },
    { code: "+32", name: "Belgium", flag: "🇧🇪", short: "BE" },
    { code: "+351", name: "Portugal", flag: "🇵🇹", short: "PT" },
    { code: "+30", name: "Greece", flag: "🇬🇷", short: "GR" },
    { code: "+972", name: "Israel", flag: "🇮🇱", short: "IL" },
    { code: "+20", name: "Egypt", flag: "🇪🇬", short: "EG" },
    { code: "+234", name: "Nigeria", flag: "🇳🇬", short: "NG" },
    { code: "+254", name: "Kenya", flag: "🇰🇪", short: "KE" },
    { code: "+92", name: "Pakistan", flag: "🇵🇰", short: "PK" },
    { code: "+880", name: "Bangladesh", flag: "🇧🇩", short: "BD" },
    { code: "+94", name: "Sri Lanka", flag: "🇱🇰", short: "LK" },
    { code: "+977", name: "Nepal", flag: "🇳🇵", short: "NP" },
];

interface PhoneInputProps {
    countryCode: string;
    phoneNumber: string;
    onCountryCodeChange: (code: string) => void;
    onPhoneNumberChange: (phone: string) => void;
    className?: string;
    error?: boolean;
}

export function PhoneInput({
    countryCode,
    phoneNumber,
    onCountryCodeChange,
    onPhoneNumberChange,
    className,
    error,
}: PhoneInputProps) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const searchInputRef = useRef<HTMLInputElement>(null);

    const selectedCountry = countries.find((c) => c.code === countryCode) || countries[0];

    const filteredCountries = countries.filter(
        (country) =>
            country.name.toLowerCase().includes(search.toLowerCase()) ||
            country.code.includes(search) ||
            country.short.toLowerCase().includes(search.toLowerCase())
    );

    useEffect(() => {
        if (open && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [open]);

    return (
        <div className={cn("flex gap-2", className)}>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className={cn(
                        "w-[110px] justify-between px-3 h-10 bg-muted/30 border-input/60 hover:bg-muted/50 rounded-lg shrink-0",
                        error && "border-destructive focus-visible:ring-destructive"
                    )}
                >
                    <span className="flex items-center gap-2">
                        <span className="text-lg leading-none">{selectedCountry.flag}</span>
                        <span className="text-sm font-medium">{selectedCountry.code}</span>
                    </span>
                    <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[280px] p-0" align="start">
                    <div className="flex items-center border-b px-3">
                        <Search className="h-4 w-4 shrink-0 opacity-50" />
                        <Input
                            ref={searchInputRef}
                            placeholder="Search country..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-11"
                        />
                    </div>
                    <div className="max-h-[300px] overflow-y-auto">
                        {filteredCountries.length === 0 ? (
                            <div className="py-6 text-center text-sm text-muted-foreground">
                                No country found.
                            </div>
                        ) : (
                            filteredCountries.map((country) => (
                                <button
                                    key={country.code}
                                    onClick={() => {
                                        onCountryCodeChange(country.code);
                                        setOpen(false);
                                        setSearch("");
                                    }}
                                    className={cn(
                                        "w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-muted/50 transition-colors",
                                        countryCode === country.code && "bg-muted"
                                    )}
                                >
                                    <span className="text-xl leading-none">{country.flag}</span>
                                    <span className="flex-1 text-left">{country.name}</span>
                                    <span className="text-muted-foreground font-medium">{country.code}</span>
                                    {countryCode === country.code && (
                                        <Check className="h-4 w-4 text-primary" />
                                    )}
                                </button>
                            ))
                        )}
                    </div>
                </PopoverContent>
            </Popover>

            <Input
                type="tel"
                placeholder="Phone number"
                value={phoneNumber}
                onChange={(e) => {
                    // Only allow numbers
                    const value = e.target.value.replace(/\D/g, "");
                    onPhoneNumberChange(value);
                }}
                className={cn(
                    "flex-1 h-10 px-4 bg-muted/30 border-input/60 focus:bg-background transition-all rounded-lg",
                    error && "border-destructive focus-visible:ring-destructive"
                )}
            />
        </div>
    );
}

// Display component for showing phone number (read-only)
export function PhoneDisplay({
    countryCode,
    phoneNumber,
    className,
}: {
    countryCode?: string | null;
    phoneNumber?: string | null;
    className?: string;
}) {
    if (!countryCode || !phoneNumber) {
        return <span className={cn("text-muted-foreground", className)}>Not provided</span>;
    }

    const country = countries.find((c) => c.code === countryCode);

    return (
        <span className={cn("flex items-center gap-2", className)}>
            {country && <span className="text-base">{country.flag}</span>}
            <span>{countryCode} {phoneNumber}</span>
        </span>
    );
}

export { countries };
